import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import axios from 'axios';
import { AppConfig, JWT } from '../../config';
import { Translate as t } from '../../config/Translate';
import { AlertAction } from '../../actions/AlertAction';
import { ConfirmDialog } from '../common/ConfirmDialog';
import {
	COMMON_TIMEZONES,
	timeSlotsToStartEndBreaks,
	startEndBreaksToTimeSlots,
	getBaseSummaryParts,
} from './workScheduleUtils';

const DAY_KEYS = ['common.day.sun', 'common.day.mon', 'common.day.tue', 'common.day.wed', 'common.day.thu', 'common.day.fri', 'common.day.sat'];

const SYSTEM_DEFAULT = {
	workDays: [1, 2, 3, 4, 5, 6],
	timeSlots: [
		{ start: '09:00', end: '12:00' },
		{ start: '13:00', end: '18:00' },
	],
	timezone: '',
};

/**
 * 조직(Company) 근무 일정 관리 섹션
 * AdminCompanyEdit 페이지에서 사용
 */
const CompanyWorkScheduleSection = ({ companyId, editable }) => {
	const dispatch = useDispatch();
	const [isLoading, setIsLoading] = useState(false);
	const [isSaving, setIsSaving] = useState(false);
	const [showEditor, setShowEditor] = useState(false);

	// 저장된 조직 스케줄 (null이면 미설정)
	const [companyBase, setCompanyBase] = useState(null);
	// Company 모델의 timezone (company.timezone 필드)
	const [companyTimezone, setCompanyTimezone] = useState('');

	// 편집 상태
	const [editTimezone, setEditTimezone] = useState('');
	const [editWorkDays, setEditWorkDays] = useState([1, 2, 3, 4, 5, 6]);
	const [editWorkStart, setEditWorkStart] = useState('09:00');
	const [editWorkEnd, setEditWorkEnd] = useState('18:00');
	const [editBreaks, setEditBreaks] = useState([{ start: '12:00', end: '13:00' }]);

	useEffect(() => {
		if (companyId && companyId !== 'ADD') {
			fetchCompanySchedule();
		}
	}, [companyId]);

	const fetchCompanySchedule = async () => {
		setIsLoading(true);
		try {
			const ret = await axios.post(
				AppConfig.API_SERVER_PATH + '/workschedule',
				{ type: 'getCompanySchedule', data: { companyId } },
				JWT()
			);
			if (ret.data.status === 'success') {
				setCompanyBase(ret.data.data.companyBase || null);
				setCompanyTimezone(ret.data.data.companyTimezone || '');
			}
		} catch (e) {
			dispatch(AlertAction.ErrorMsg('Failed to load work schedule.'));
		}
		setIsLoading(false);
	};

	const openEditor = () => {
		const base = companyBase || SYSTEM_DEFAULT;
		const parsed = timeSlotsToStartEndBreaks(base.timeSlots);
		setEditTimezone(base.timezone || companyTimezone || '');
		setEditWorkDays([...(base.workDays || SYSTEM_DEFAULT.workDays)]);
		setEditWorkStart(parsed.workStart);
		setEditWorkEnd(parsed.workEnd);
		setEditBreaks(parsed.breaks.length > 0 ? parsed.breaks : []);
		setShowEditor(true);
	};

	const handleSave = async () => {
		setIsSaving(true);
		try {
			const timeSlots = startEndBreaksToTimeSlots(editWorkStart, editWorkEnd, editBreaks);
			const ret = await axios.post(
				AppConfig.API_SERVER_PATH + '/workschedule',
				{
					type: 'saveCompanyBase',
					data: { companyId, workDays: editWorkDays, timeSlots, timezone: editTimezone }
				},
				JWT()
			);
			if (ret.data.status === 'success') {
				dispatch(AlertAction.Success(t('common.message.success.saved')));
				await fetchCompanySchedule();
				setShowEditor(false);
			} else {
				dispatch(AlertAction.ErrorMsg('Failed to save work schedule.'));
			}
		} catch (e) {
			dispatch(AlertAction.ErrorMsg('Failed to save work schedule.'));
		}
		setIsSaving(false);
	};

	// window.confirm 대체: 버튼은 대상 companyId 를 상태에 담아 확인 대화상자만 열고, 확인 시 그 값으로 실행
	const [pendingResetCompanyId, setPendingResetCompanyId] = useState(null);
	const handleReset = () => setPendingResetCompanyId(companyId || null);

	const doReset = async (targetCompanyId) => {
		if (!targetCompanyId) return;
		setIsSaving(true);
		try {
			const ret = await axios.post(
				AppConfig.API_SERVER_PATH + '/workschedule',
				{ type: 'resetCompanyBase', data: { companyId: targetCompanyId } },
				JWT()
			);
			if (ret.data.status === 'success') {
				dispatch(AlertAction.Success(t('common.message.success.saved')));
				await fetchCompanySchedule();
				setShowEditor(false);
			} else {
				dispatch(AlertAction.ErrorMsg('Failed to reset work schedule.'));
			}
		} catch (e) {
			dispatch(AlertAction.ErrorMsg('Failed to reset work schedule.'));
		}
		setIsSaving(false);
	};

	const toggleDay = (idx) => {
		setEditWorkDays(prev =>
			prev.includes(idx) ? prev.filter(d => d !== idx) : [...prev, idx].sort()
		);
	};

	const addBreak = () => setEditBreaks(prev => [...prev, { start: '12:00', end: '13:00' }]);
	const removeBreak = (idx) => setEditBreaks(prev => prev.filter((_, i) => i !== idx));
	const updateBreak = (idx, field, val) =>
		setEditBreaks(prev => prev.map((b, i) => i === idx ? { ...b, [field]: val } : b));

	const displayBase = companyBase || { ...SYSTEM_DEFAULT, timezone: companyTimezone };
	const summary = getBaseSummaryParts(displayBase, t, DAY_KEYS);

	return (
		<>
			<div className="col-12 ps-2 pe-2 mb-2">
				<div className="card">
					<div className="card-header fw-bold d-flex align-items-center justify-content-between">
						<span>{t('user.workSchedule.companySchedule')}</span>
						{editable && (
							<button className="btn btn-sm btn-outline-primary" onClick={openEditor} disabled={isLoading}>
								<ion-icon name="create-outline"></ion-icon>
							</button>
						)}
					</div>
					<div className="card-body py-2">
						{isLoading ? (
							<p className="text-muted small mb-0">{t('service.issueManager.processing')}</p>
						) : (
							<div className="small">
								{!companyBase && (
									<span className="badge bg-secondary me-2 mb-1">{t('user.workSchedule.appliedSourceSystem')}</span>
								)}
								{displayBase.timezone && (
									<div><strong>{t('user.workSchedule.timezone')}:</strong> {displayBase.timezone}</div>
								)}
								<div><strong>{t('user.workSchedule.workDays')}:</strong> {summary.days}</div>
								<div><strong>{t('user.workSchedule.timeSlots')}:</strong> {summary.timeRange}</div>
								{summary.breakRange && (
									<div><strong>Break:</strong> {summary.breakRange}</div>
								)}
							</div>
						)}
					</div>
				</div>
			</div>

			{/* Editor Modal */}
			{showEditor && (
				<div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
					<div className="modal-dialog">
						<div className="modal-content">
							<div className="modal-header">
								<h5 className="modal-title">{t('user.workSchedule.companySchedule')}</h5>
								<button type="button" className="btn-close" onClick={() => setShowEditor(false)} disabled={isSaving}></button>
							</div>
							<div className="modal-body">
								{/* Timezone */}
								<div className="mb-3">
									<label className="form-label fw-bold">{t('user.workSchedule.timezone')}</label>
									<select
										className="form-select form-select-sm"
										value={editTimezone}
										onChange={(e) => setEditTimezone(e.target.value)}
									>
										<option value="">{t('user.workSchedule.sameAsCompany')} (Not set)</option>
										{COMMON_TIMEZONES.map(tz => (
											<option key={tz.value} value={tz.value}>{tz.label}</option>
										))}
									</select>
								</div>

								{/* Work Days */}
								<div className="mb-3">
									<label className="form-label fw-bold">{t('user.workSchedule.workDays')}</label>
									<div className="d-flex flex-wrap gap-2">
										{DAY_KEYS.map((key, idx) => (
											<button
												key={idx}
												type="button"
												className={`btn btn-sm ${editWorkDays.includes(idx) ? 'btn-primary' : 'btn-outline-secondary'}`}
												onClick={() => toggleDay(idx)}
											>
												{t(key)}
											</button>
										))}
									</div>
								</div>

								{/* Work Hours */}
								<div className="mb-3">
									<label className="form-label fw-bold">{t('user.workSchedule.timeSlots')}</label>
									<div className="row g-2">
										<div className="col-6">
											<input
												type="time"
												className="form-control"
												value={editWorkStart}
												onChange={(e) => setEditWorkStart(e.target.value)}
											/>
										</div>
										<div className="col-6">
											<input
												type="time"
												className="form-control"
												value={editWorkEnd}
												onChange={(e) => setEditWorkEnd(e.target.value)}
											/>
										</div>
									</div>
								</div>

								{/* Breaks */}
								<div className="mb-3">
									<label className="form-label fw-bold">Break</label>
									{editBreaks.map((brk, idx) => (
										<div key={idx} className="row g-2 mb-2 align-items-center">
											<div className="col-5">
												<input
													type="time"
													className="form-control form-control-sm"
													value={brk.start}
													onChange={(e) => updateBreak(idx, 'start', e.target.value)}
												/>
											</div>
											<div className="col-5">
												<input
													type="time"
													className="form-control form-control-sm"
													value={brk.end}
													onChange={(e) => updateBreak(idx, 'end', e.target.value)}
												/>
											</div>
											<div className="col-2">
												<button
													type="button"
													className="btn btn-sm btn-outline-danger"
													onClick={() => removeBreak(idx)}
												>
													<ion-icon name="close-outline"></ion-icon>
												</button>
											</div>
										</div>
									))}
									<button type="button" className="btn btn-sm btn-outline-primary" onClick={addBreak}>
										+ {t('user.workSchedule.addTimeSlot')}
									</button>
								</div>
							</div>
							<div className="modal-footer">
								{companyBase && (
									<button
										type="button"
										className="btn btn-outline-warning btn-sm"
										onClick={handleReset}
										disabled={isSaving}
									>
										{t('user.workSchedule.resetToDefault')}
									</button>
								)}
								<button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowEditor(false)} disabled={isSaving}>
									{t('common.button.cancel')}
								</button>
								<button type="button" className="btn btn-primary btn-sm" onClick={handleSave} disabled={isSaving}>
									{isSaving ? t('service.issueManager.processing') : t('common.button.save')}
								</button>
							</div>
						</div>
					</div>
				</div>
			)}

			<ConfirmDialog
				show={!!pendingResetCompanyId}
				onClose={() => setPendingResetCompanyId(null)}
				onConfirm={() => doReset(pendingResetCompanyId)}
				title={t('common.message.confirm.areYouSure')}
				message={t('user.workSchedule.resetToDefault') + '?'}
				confirmLabel={t('user.workSchedule.resetToDefault')}
				confirmCss="btn btn-block btn-warning" />
		</>
	);
};

export default CompanyWorkScheduleSection;
